import { useEffect } from "react";
import { Outlet, Navigate } from "react-router-dom";
import {
  useAuthSession,
  useAuthUser,
} from "@/components/providers/auth-context";
import { useProvisioning } from "@/lib/hooks/use-provisioning";
import {
  OnboardingHeader,
  ProgressIndicator,
  type StepDefinition,
} from "@/components/onboarding";
import { useOnboardingStore } from "@/lib/stores/onboarding-store";

const STEPS: StepDefinition[] = [
  { id: "org", label: "Organization", path: "/onboarding/org" },
  { id: "tier", label: "Select Plan", path: "/onboarding/tier" },
  { id: "site", label: "Add Site", path: "/onboarding/site" },
  { id: "setup", label: "Setup", path: "/onboarding/setup" },
];

export function OnboardingLayout() {
  const { isAuthenticated, isSessionLoading } = useAuthSession();
  const { isUserLoading } = useAuthUser();
  const { isProvisioned, isProvisioning, provisioningError, profile } =
    useProvisioning();
  const { completedSteps, completeStep } = useOnboardingStore();

  // Sync completed steps from backend state
  // This handles the case where localStorage is cleared but backend has state
  useEffect(() => {
    if (!profile?.tenant) return;

    // If org setup is complete on backend but not in local state, sync it
    if (
      profile.tenant.isOrgSetupComplete &&
      !completedSteps.includes("org")
    ) {
      completeStep("org");
    }
  }, [profile, completedSteps, completeStep]);

  if (isSessionLoading || isUserLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isProvisioning) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (provisioningError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <div className="text-destructive">Failed to set up your account</div>
        <div className="text-sm text-muted-foreground">
          {provisioningError.message}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-primary underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!isProvisioned) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <OnboardingHeader />
      <main className="container mx-auto max-w-2xl px-4 py-8">
        <ProgressIndicator steps={STEPS} />
        <div className="mt-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
